import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Disc3,
  Filter,
  Gauge,
  Library,
  Lock,
  Music2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Unlock,
  X,
  Youtube,
} from "lucide-react";
import { PaginationMeta } from "../../types/api";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import {
  deleteSongV2,
  fetchSongsV2,
  SongV2ListItem,
} from "./songV2Api";
import styles from "./SongV2Shared.module.css";

const PAGE_SIZE = 100;
type SortMode = "title" | "artist" | "bpm" | "updated_at";

const formatter = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatter.format(date);
}

function formatDuration(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

export default function SongV2List() {
  const navigate = useNavigate();
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const canDelete = Boolean(access?.canDeleteSongs);
  const [songs, setSongs] = useState<SongV2ListItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SongV2ListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [artist, setArtist] = useState("");
  const [sourceType, setSourceType] = useState("all");
  const [lockFilter, setLockFilter] = useState<"all" | "locked" | "open">("all");
  const [sortMode, setSortMode] = useState<SortMode>("updated_at");
  const [sortAscending, setSortAscending] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadSongs = useCallback(
    async (refresh = false) => {
      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");
        const response = await fetchSongsV2({
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          artist: artist.trim() || undefined,
          source_type: sourceType === "all" ? undefined : sourceType,
          is_locked:
            lockFilter === "all" ? undefined : lockFilter === "locked",
        });
        setSongs(response.data);
        setMeta(response.meta);
      } catch (err: any) {
        setError(err?.message || "Şarkılar yüklenemedi.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [artist, lockFilter, page, search, sourceType]
  );

  useEffect(() => {
    loadSongs();
  }, [loadSongs]);

  useEffect(() => {
    setPage(1);
  }, [artist, lockFilter, sourceType]);

  const sortedSongs = useMemo(() => {
    return [...songs].sort((a, b) => {
      const direction = sortAscending ? 1 : -1;
      if (sortMode === "bpm") return (a.bpm - b.bpm) * direction;
      if (sortMode === "updated_at") {
        return (
          (new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime()) *
          direction
        );
      }
      return (
        a[sortMode].localeCompare(b[sortMode], "tr", {
          sensitivity: "base",
        }) * direction
      );
    });
  }, [songs, sortAscending, sortMode]);

  const stats = useMemo(() => {
    const locked = songs.filter((song) => song.is_locked).length;
    const youtube = songs.filter(
      (song) => song.source.type.toLowerCase() === "youtube"
    ).length;
    const averageBpm = songs.length
      ? Math.round(
          songs.reduce((total, song) => total + song.bpm, 0) / songs.length
        )
      : 0;
    return { locked, youtube, averageBpm };
  }, [songs]);

  const total = Number(meta?.total ?? songs.length);
  const totalPages = Math.max(1, Number(meta?.total_pages || 1));
  const currentPage = Number(meta?.page || page);
  const hasFilters =
    Boolean(searchInput || artist) ||
    sourceType !== "all" ||
    lockFilter !== "all";

  const remove = async () => {
    if (!deleteTarget || !canDelete) return;
    try {
      setDeleting(true);
      await deleteSongV2(deleteTarget.songv2_id);
      setDeleteTarget(null);
      setMessage(`“${deleteTarget.title}” silindi.`);
      await loadSongs(true);
    } catch (err: any) {
      setError(err?.message || "Şarkı silinemedi.");
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setArtist("");
    setSourceType("all");
    setLockFilter("all");
  };

  return (
    <div className={styles.songPage}>
      <header className={styles.songHero}>
        <div>
          <span><Music2 size={14} /> V2 içerik kütüphanesi</span>
          <h1>Şarkılar</h1>
          <p>
            V2 şarkı kataloğunu, zamanlama ayarlarını ve davul chart
            içeriklerini yönetin.
          </p>
        </div>
        <div>
          <button type="button" onClick={() => loadSongs(true)} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? styles.spinning : ""} />
            Yenile
          </button>
          {canWrite && <Link to="/songs/add"><Plus size={17} /> Yeni şarkı</Link>}
        </div>
      </header>

      <section className={styles.songStats}>
        <article><span className={styles.statBlue}><Library size={18} /></span><div><strong>{total}</strong><small>Toplam V2 şarkı</small></div></article>
        <article><span className={styles.statGreen}><Youtube size={18} /></span><div><strong>{stats.youtube}</strong><small>YouTube kaynaklı</small></div></article>
        <article><span className={styles.statViolet}><Lock size={18} /></span><div><strong>{stats.locked}</strong><small>Kilitli içerik</small></div></article>
        <article><span className={styles.statAmber}><Gauge size={18} /></span><div><strong>{stats.averageBpm}</strong><small>Ortalama BPM</small></div></article>
      </section>

      {(error || message) && (
        <div className={error ? styles.songFeedbackError : styles.songFeedbackSuccess}>
          {error ? <AlertCircle size={17} /> : <ShieldCheck size={17} />}
          <span>{error || message}</span>
          <button type="button" onClick={() => { setError(""); setMessage(""); }}><X size={15} /></button>
        </div>
      )}

      <section className={styles.songDirectory}>
        <div className={styles.directoryHeading}>
          <div><span>Hafif liste modeli</span><h2>Şarkı dizini</h2></div>
          <span>{total.toLocaleString("tr-TR")} kayıt</span>
        </div>
        <div className={styles.songToolbar}>
          <label className={styles.songSearch}>
            <Search size={16} />
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Şarkı veya sanatçı ara..." />
            {searchInput && <button type="button" onClick={() => setSearchInput("")}><X size={14} /></button>}
          </label>
          <label>
            <Music2 size={14} />
            <input value={artist} onChange={(event) => setArtist(event.target.value)} placeholder="Sanatçı filtresi" />
          </label>
          <label>
            <Disc3 size={14} />
            <select value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
              <option value="all">Tüm kaynaklar</option>
              <option value="youtube">YouTube</option>
            </select>
          </label>
          <label>
            <Filter size={14} />
            <select value={lockFilter} onChange={(event) => setLockFilter(event.target.value as "all" | "locked" | "open")}>
              <option value="all">Tüm erişimler</option>
              <option value="locked">Kilitli</option>
              <option value="open">Açık</option>
            </select>
          </label>
          <button type="button" className={styles.sortButton} aria-label="Sıralama yönü" onClick={() => setSortAscending((current) => !current)}>
            {sortAscending ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </button>
        </div>
        <div className={styles.secondaryToolbar}>
          <label>
            <span>Sırala</span>
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)}>
              <option value="updated_at">Güncellenme tarihi</option>
              <option value="title">Başlık</option>
              <option value="artist">Sanatçı</option>
              <option value="bpm">BPM</option>
            </select>
          </label>
          {hasFilters && <button type="button" onClick={resetFilters}>Filtreleri temizle</button>}
        </div>

        {loading ? (
          <div className={styles.songState}><div className={styles.songLoader} /><h2>V2 katalog yükleniyor</h2><p>Hafif şarkı kayıtları hazırlanıyor.</p></div>
        ) : sortedSongs.length === 0 ? (
          <div className={styles.songState}><Music2 size={32} /><h2>Şarkı bulunamadı</h2><p>Filtreleri değiştirerek tekrar deneyin.</p></div>
        ) : (
          <>
            <div className={styles.songTableWrap}>
              <table className={styles.songTable}>
                <thead><tr><th>Şarkı</th><th>Kaynak</th><th>Tempo</th><th>Süre</th><th>Erişim</th><th>Güncellendi</th><th /></tr></thead>
                <tbody>
                  {sortedSongs.map((song) => (
                    <tr key={song.songv2_id} onClick={() => navigate(`/songs/${song.songv2_id}`)}>
                      <td><div className={styles.songIdentity}><span><Music2 size={18} /></span><div><strong>{song.title}</strong><p>{song.artist}</p><small>{song.songv2_id}</small></div></div></td>
                      <td><span className={styles.sourceBadge}><Youtube size={13} /> {song.source.type}</span></td>
                      <td><strong className={styles.tempo}>{song.bpm} BPM</strong><small className={styles.rhythm}>{song.ts}</small></td>
                      <td>{formatDuration(song.duration_ms)}</td>
                      <td>{song.is_locked ? <span className={styles.lockedBadge}><Lock size={12} /> Kilitli</span> : <span className={styles.openBadge}><Unlock size={12} /> Açık</span>}</td>
                      <td>{formatDate(song.updated_at)}</td>
                      <td><div className={styles.rowActions}>
                        {canDelete && <button type="button" className={styles.dangerAction} onClick={(event) => { event.stopPropagation(); setDeleteTarget(song); }}><Trash2 size={15} /></button>}
                        <button type="button" aria-label="Detayı aç"><ArrowRight size={16} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.songMobileGrid}>
              {sortedSongs.map((song) => (
                <button type="button" key={song.songv2_id} onClick={() => navigate(`/songs/${song.songv2_id}`)}>
                  <div className={styles.mobileTop}><span><Music2 size={18} /></span><div><strong>{song.title}</strong><p>{song.artist}</p></div>{song.is_locked ? <Lock size={14} /> : <Unlock size={14} />}</div>
                  <div className={styles.mobileMeta}><span><Youtube size={12} /> {song.source.type}</span><span><Gauge size={12} /> {song.bpm} BPM</span><span><Clock3 size={12} /> {formatDuration(song.duration_ms)}</span></div>
                  <small>{song.songv2_id}</small>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {totalPages > 1 && (
        <nav className={styles.songPagination}>
          <span>{total} kayıttan {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)}</span>
          <div><button type="button" disabled={currentPage <= 1 || loading} onClick={() => setPage((current) => current - 1)}><ChevronLeft size={16} /> Önceki</button><span>Sayfa {currentPage} / {totalPages}</span><button type="button" disabled={currentPage >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>Sonraki <ChevronRight size={16} /></button></div>
        </nav>
      )}

      {deleteTarget && (
        <div className={styles.songModalOverlay} role="presentation" onMouseDown={() => !deleting && setDeleteTarget(null)}>
          <section role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <span><Trash2 size={22} /></span><h2>Şarkı silinsin mi?</h2><p><strong>{deleteTarget.title}</strong> kalıcı olarak silinecek.</p>
            <div><button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Vazgeç</button><button type="button" onClick={remove} disabled={deleting}><Trash2 size={15} /> {deleting ? "Siliniyor" : "Şarkıyı sil"}</button></div>
          </section>
        </div>
      )}
    </div>
  );
}
